import { useEffect, useState } from "react";

/** True once the viewport is at least `minWidth` px wide; updates reactively on resize. */
export function useMinWidth(minWidth: number): boolean {
  const [matches, setMatches] = useState(() => (typeof window === "undefined" ? true : window.innerWidth >= minWidth));

  useEffect(() => {
    const onResize = () => setMatches(window.innerWidth >= minWidth);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [minWidth]);

  return matches;
}

// A plain width>=1000 check puts every iPad except the 12.9" Pro into the
// drawer nav the instant it's held in portrait — every iPad's portrait
// width (mini 768, standard/Air 810-820, Pro 11" 834) sits under 1000, even
// though it's a full tablet, not a phone. No real phone (portrait or
// landscape) reaches 740px wide, so requiring both a lower width floor AND
// a tall portrait-shaped viewport (height >= 900) picks up every iPad in
// portrait without reclassifying any actual phone.
function computeIsDesktop(): boolean {
  if (typeof window === "undefined") return true;
  const w = window.innerWidth;
  const h = window.innerHeight;
  return w >= 1000 || (w >= 740 && h >= 900);
}

/** Matches the mockup's shell breakpoint: >=1000px gets the fixed sidebar; below it, a drawer — widened to also cover a tablet held in portrait (see computeIsDesktop's doc). */
export function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(computeIsDesktop);

  useEffect(() => {
    const onResize = () => setMatches(computeIsDesktop());
    onResize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return matches;
}

/** Matches the mockup's tablet breakpoint (`MID`) used by most 2-column content grids. */
export function useIsWide(): boolean {
  return useMinWidth(640);
}
